import {
  type LineageModificationsMap,
  type Modification,
  type ModificationsMap,
  type ProductIngredients,
} from '../../types';
import { decodeIngredientId } from './decode-ingredient-id';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Generates a list of modifications based on the provided ingredients and modifiers map.
 * It accumulates and updates modifications with respect to the available ingredient portions.
 *
 * When a lineage map is provided, ingredients whose id is not on the current
 * product fall back to the modification sharing their lineage key. This lets a
 * line item from another restaurant resolve against the local menu.
 *
 * When restoring a line item, ids missing from the product also resolve by their
 * raw ingredient id, and those fallbacks never fill a group past its limit.
 */
export function generateModificationsList(
  ingredients: ProductIngredients,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: LineageModificationsMap,
  lineItemRestore?: LineItemRestore,
) {
  // A lineage key can match several modifications on this product. Give each
  // distinct incoming ingredient id its own slot so portions stay spread across
  // groups the way the product offers them, instead of collapsing onto one and
  // breaching that group's aggregate limit.
  const claimedByIngredientId = new Map<string, Modification>();
  const claimedSlots = new Set<string>();

  const modificationsMap = ingredients.reduce<ModificationsMap>((map, ingredient) => {
    const modification = claimModification({
      ingredient,
      allModificationsMap,
      lineageModificationsMap,
      lineItemRestore,
      modificationsMap: map,
      claimedByIngredientId,
      claimedSlots,
    }); // prettier-ignore

    // ─── Invalid modification ────────────────────────────────────────────

    if (!modification) return map;

    // The fallback is just only satisfies the nullable generated type.
    const ingredientId = modification.ingredient?.id ?? ingredient.id;

    const maybeExistingModification = map.get(ingredientId);

    // ─── Bump quantity if multiple portions are available ────────────────

    if (maybeExistingModification) {
      map.set(ingredientId, withOutOfStockFlag(withUpdatedQuantity(maybeExistingModification))); // prettier-ignore

      return map;
    }

    // ─── Initial portion ─────────────────────────────────────────────────

    map.set(ingredientId, withOutOfStockFlag(modification));

    return map;
  }, new Map());

  return {
    modificationsMap,
    modificationsList: [...modificationsMap.values()],
  };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolves the product modification for an ingredient by id, then by lineage key.
 * Falls back to the first modification sharing the key, for callers that resolve
 * a single ingredient with no notion of which slot is still free.
 */
export function resolveModification(
  ingredient: Pick<ProductIngredients[number], 'id' | 'lineageKey'>,
  allModificationsMap: ModificationsMap,
  lineageModificationsMap?: LineageModificationsMap,
) {
  const modificationById = allModificationsMap.get(ingredient.id);

  if (modificationById) {
    return modificationById;
  }

  const lineageKey = ingredient.lineageKey;

  if (!lineageKey || !lineageModificationsMap) {
    return;
  }

  return lineageModificationsMap.get(lineageKey)?.[0];
}

/**
 * Resolves the product modification for an ingredient id missing from the product
 * by its raw ingredient id, preferring the modifier group encoded in the id.
 */
export function resolveModificationByRawIngredientId(
  ingredientId: string,
  allModificationsMap: ModificationsMap,
) {
  const candidates = findRawIngredientCandidates({ ingredientId, allModificationsMap });

  return findInEncodedGroup({ ingredientId, candidates }) ?? candidates[0];
}

/**
 * Resolves a modification while tracking which lineage slots are already taken,
 * so repeated portions of one ingredient reuse their slot and distinct
 * ingredients take distinct ones. An id missing from the product resolves by its
 * raw ingredient id before its lineage key, and never into a group that is full.
 */
function claimModification(params: ClaimModificationParams) {
  const {
    ingredient,
    allModificationsMap,
    lineageModificationsMap,
    lineItemRestore,
    claimedByIngredientId,
    claimedSlots,
  } = params;

  const modificationById = allModificationsMap.get(ingredient.id);

  if (modificationById) {
    return modificationById;
  }

  const ingredientId = ingredient.id;
  const rawCandidates = lineItemRestore
    ? findRawIngredientCandidates({ ingredientId, allModificationsMap })
    : [];

  if (rawCandidates.length > 0) {
    const openRawCandidates = filterOpenCandidates({ ...params, candidates: rawCandidates });
    const encodedRawCandidate = findInEncodedGroup({ ingredientId, candidates: openRawCandidates });

    return encodedRawCandidate ?? openRawCandidates[0];
  }

  const lineageKey = ingredient.lineageKey;

  if (!lineageKey || !lineageModificationsMap) return;

  const alreadyClaimed = claimedByIngredientId.get(ingredient.id);
  const isAlreadyClaimedOpen = checkIfOpenCandidate({ ...params, candidate: alreadyClaimed });

  if (alreadyClaimed && isAlreadyClaimedOpen) {
    return alreadyClaimed;
  }

  const candidates = lineageModificationsMap.get(lineageKey) ?? [];
  const openCandidates = filterOpenCandidates({ ...params, candidates });
  const encodedGroupCandidate = findInEncodedGroup({ ingredientId, candidates: openCandidates });
  const freeCandidate = openCandidates.find((candidate) => !claimedSlots.has(slotKey(candidate)));
  const claimed = encodedGroupCandidate ?? freeCandidate ?? openCandidates[0];

  if (!claimed) return;

  claimedByIngredientId.set(ingredient.id, claimed);
  claimedSlots.add(slotKey(claimed));

  return claimed;
}

function slotKey(modification: Modification) {
  return `${modification.ingredient?.id ?? modification.ingredientId}-${modification.modificationGroupId}`;
}

function withUpdatedQuantity(modification: Modification): Modification {
  return { ...modification, quantity: modification.quantity + 1 };
}

function withOutOfStockFlag(modification: Modification): Modification {
  return { ...modification, outOfStock: modification.outOfStock };
}

function findRawIngredientCandidates(params: FindRawIngredientCandidatesParams) {
  const { ingredientId, allModificationsMap } = params;

  const { rawIngredientId } = decodeIngredientId(ingredientId);

  return [...allModificationsMap.values()].filter(
    (modification) => modification.ingredientId === rawIngredientId,
  );
}

function filterOpenCandidates(params: FilterOpenCandidatesParams) {
  const { candidates } = params;

  return candidates.filter((candidate) => checkIfOpenCandidate({ ...params, candidate }));
}

function findInEncodedGroup(params: FindInEncodedGroupParams) {
  const { ingredientId, candidates } = params;

  const { modificationGroupId } = decodeIngredientId(ingredientId);

  if (!modificationGroupId) {
    return;
  }

  return candidates.find((candidate) => candidate.modificationGroupId === modificationGroupId);
}

function checkIfOpenCandidate(params: CheckIfOpenCandidateParams) {
  const { candidate, modificationsMap, lineItemRestore } = params;

  if (!candidate) {
    return false;
  }

  const { modificationGroupId } = candidate;

  const groupLimits = lineItemRestore?.modificationGroupLimits;
  const groupLimit = groupLimits?.get(modificationGroupId) ?? Number.POSITIVE_INFINITY;
  const groupQuantity = [...modificationsMap.values()]
    .filter((modification) => modification.modificationGroupId === modificationGroupId)
    .reduce(sumQuantity, 0);

  return groupQuantity < groupLimit;
}

function sumQuantity(total: number, modification: Modification) {
  return total + modification.quantity;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ClaimModificationParams = {
  ingredient: ProductIngredients[number];
  allModificationsMap: ModificationsMap;
  lineageModificationsMap?: LineageModificationsMap;
  lineItemRestore?: LineItemRestore;
  modificationsMap: ModificationsMap;
  claimedByIngredientId: Map<string, Modification>;
  claimedSlots: Set<string>;
};

type FindRawIngredientCandidatesParams = Pick<ClaimModificationParams, 'allModificationsMap'> & {
  ingredientId: string;
};

type FilterOpenCandidatesParams = Pick<
  ClaimModificationParams,
  'modificationsMap' | 'lineItemRestore'
> & { candidates: Modification[] };

type FindInEncodedGroupParams = {
  ingredientId: string;
  candidates: Modification[];
};

type CheckIfOpenCandidateParams = Pick<
  ClaimModificationParams,
  'modificationsMap' | 'lineItemRestore'
> & { candidate?: Modification };

type LineItemRestore = {
  modificationGroupLimits: ReadonlyMap<string, number>;
};
